import { distance2D } from '@paragon/utilities';

import Direction2D from './Direction2D';
import Line2D from './Line2D';
import LineSegment2D from './LineSegment2D';
import Point3D, { Point3DInterface } from './Point3D';
import { convertDegreesToRadians, distanceEquals } from './units';
import Vector2D from './Vector2D';

export interface Point2DInterface {
  readonly x: number;
  readonly y: number;
}

export function point2DDistanceTo(a: Point2DInterface, b: Point2DInterface) {
  return distance2D(a.x, a.y, b.x, b.y);
}

export function point2DEquals(a: Point2DInterface, b: Point2DInterface) {
  return distanceEquals(point2DDistanceTo(a, b), 0);
}

export function point2DTo3D(point: Point2DInterface, z = 0): Point3DInterface {
  return { x: point.x, y: point.y, z };
}

class Point2D {
  static readonly origin = new Point2D(0, 0);

  readonly x: number;
  readonly y: number;

  get vectorFromOrigin() {
    return new Vector2D(this.x, this.y);
  }

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  static fromObject(point: Point2DInterface) {
    return new Point2D(point.x, point.y);
  }

  setX(x: number) {
    return new Point2D(x, this.y);
  }

  setY(y: number) {
    return new Point2D(this.x, y);
  }

  add(point: Point2DInterface) {
    return new Point2D(this.x + point.x, this.y + point.y);
  }

  subtract(point: Point2DInterface) {
    return new Point2D(this.x - point.x, this.y - point.y);
  }

  multiplyScalar(scalar: number) {
    return new Point2D(this.x * scalar, this.y * scalar);
  }

  rotate(angle: number) {
    const cosTheta = Math.cos(convertDegreesToRadians(angle));
    const sinTheta = Math.sin(convertDegreesToRadians(angle));

    return new Point2D(cosTheta * this.x - sinTheta * this.y, sinTheta * this.x + cosTheta * this.y);
  }

  rotateAround(point: Point2DInterface, angle: number) {
    return this.subtract(point).rotate(angle).add(point);
  }

  distanceTo(point: Point2DInterface) {
    return point2DDistanceTo(this, point);
  }

  distanceToLine(line: Line2D) {
    const projected = this.projectOntoLine(line);
    return this.distanceTo(projected);
  }

  directionToPoint(point: Point2D) {
    return Direction2D.fromPoints(this, point);
  }

  directionToLine(line: Line2D) {
    return Direction2D.fromPoints(this, this.projectOntoLine(line));
  }

  directionFromLine(line: Line2D) {
    return this.directionToLine(line).reverse();
  }

  distanceToLineSegment(lineSegment: LineSegment2D) {
    const projected = this.projectOntoLineSegment(lineSegment);
    return projected ? this.distanceTo(projected) : Math.min(...lineSegment.endPoints.map(this.distanceTo.bind(this)));
  }

  projectOntoLine(line: Line2D) {
    const hypotenuse = Vector2D.fromPointAndPoint(line.basePoint, this);
    const dotProduct = hypotenuse.direction.dotProduct(line.direction);
    return line.getPointAlongLine(dotProduct * hypotenuse.magnitude);
  }

  projectOntoLineSegment(lineSegment: LineSegment2D) {
    const result = this.projectOntoLine(lineSegment.toLine());
    return lineSegment.contains(result) ? result : null;
  }

  projectedSignedDistanceAlong(line: Line2D) {
    return Vector2D.fromPointAndPoint(line.basePoint, this).getComponentInDirection(line.direction);
  }

  signedDistanceAlong(line: Line2D) {
    if (!line.containsPoint(this)) throw new Error('The point should be on the line.');
    return this.projectedSignedDistanceAlong(line);
  }

  getCoordinate(direction: Direction2D): number {
    return Vector2D.fromOriginTo(this).dotProduct(direction.toVector(1));
  }

  isOnLine(line: Line2D): boolean {
    return line.containsPoint(this);
  }

  equals(point: Point2DInterface) {
    return point2DEquals(this, point);
  }

  translate(vector: Vector2D) {
    return new Point2D(this.x + vector.x, this.y + vector.y);
  }

  toArray(): [number, number] {
    return [this.x, this.y];
  }

  to3D(z = 0): Point3D {
    return new Point3D(this.x, this.y, z);
  }

  toObject(): Point2DInterface {
    return { x: this.x, y: this.y };
  }
}

export default Point2D;
